import { useState } from "react";
import { Link } from "react-router-dom";
import { Activity, ArrowLeft, CheckCircle2, FileWarning, Loader2, Mail, MessageCircle, Phone, RefreshCw, Send, ShieldCheck, Ticket, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";
import Turnstile, { turnstileActive } from "../components/Turnstile";
import { useHoneypot } from "../components/Honeypot";

const CONTENT = {
  it: {
    eyebrow: "Carta dei Servizi",
    title: "I nostri impegni, misurabili",
    intro: "Questa Carta dei Servizi dichiara i livelli di qualità che SIMAMMA si impegna a garantire su piattaforma, consegna eSIM e assistenza. I valori sono target misurati su base mensile.",
    updated: "Ultimo aggiornamento: 21 settembre 2026",
    slaTitle: "Livelli di servizio (SLA)",
    slaSub: "Cosa puoi aspettarti, sempre",
    sla: [
      { icon: "activity", value: "99,9%", label: "Disponibilità piattaforma", desc: "Uptime mensile target di sito e app, con monitoraggio continuo e intervento automatico." },
      { icon: "zap", value: "< 2 min", label: "Consegna eSIM", desc: "Dal pagamento alla disponibilità del QR in area personale e via email, 24 ore su 24, festivi inclusi." },
      { icon: "refresh", value: "< 5 min", label: "Ricariche dati", desc: "I top-up si applicano alla eSIM esistente: nessuna reinstallazione, nessuna attesa in viaggio." },
      { icon: "chat", value: "Immediata", label: "Assistente automatico", desc: "La chat AI risponde subito, 24/7, in 10 lingue: piani, compatibilità, ordini e installazione." },
      { icon: "mail", value: "< 24h lav.", label: "Email e form contatti", desc: "Ogni richiesta scritta riceve risposta da una persona entro un giorno lavorativo." },
      { icon: "shield", value: "Automatico", label: "Rimborso se la consegna fallisce", desc: "Se la eSIM non viene consegnata per un problema tecnico, il rimborso parte in automatico: zero moduli, zero attese." },
      { icon: "file", value: "≤ 5 giorni lav.", label: "Gestione reclami", desc: "Ogni reclamo riceve una risposta motivata e tracciata entro cinque giorni lavorativi." },
    ],
    chanTitle: "Canali di contatto e tempi di risposta",
    chanSub: "Scegli il canale che preferisci: i tempi pubblicati sono il nostro impegno",
    chanHead: ["Canale", "Per cosa", "Tempo di risposta"],
    chan: [
      { icon: "chat", name: "Chat assistente AI", for: "Info su piani, copertura, compatibilità, stato ordini", time: "Immediata — 24/7" },
      { icon: "ticket", name: "Ticket dall'area riservata", for: "Clienti registrati: ordini, ricariche, account", time: "Presa in carico entro 2h, risposta entro 24h lavorative" },
      { icon: "mail", name: "Email info@vediloo.it", for: "Amministrazione, fatture, privacy, partnership", time: "Entro 24h lavorative" },
      { icon: "phone", name: "Telefono +39 06 84102697", for: "Urgenze e amministrazione", time: "Orario d'ufficio (CET)" },
      { icon: "send", name: "Form in questa pagina", for: "Chiunque, anche senza account", time: "Conferma immediata, risposta entro 24h lavorative" },
    ],
    formTitle: "Scrivici ora",
    formSub: "Compila il form: ricevi subito la conferma di presa in carico e una risposta entro 24 ore lavorative.",
    fName: "Nome e cognome",
    fEmail: "La tua email",
    fSubject: "Oggetto",
    fMessage: "Messaggio",
    fSend: "Invia messaggio",
    fDoneTitle: "Messaggio inviato",
    fDoneBody: "Grazie! Ti abbiamo appena inviato la conferma di presa in carico. Risponderemo a",
    fDoneWithin: "entro 24 ore lavorative.",
    fAgain: "Invia un altro messaggio",
    noteTitle: "Nota metodologica",
    noteBody: "I livelli di servizio sono misurati su base mensile sui sistemi SIMAMMA (sito, app, API, consegna eSIM). La disponibilità e la qualità della rete dati mobile dipendono dagli operatori locali partner e non rientrano in questi SLA. In caso di superamento dei tempi di risposta scrivi a info@vediloo.it citando questa Carta.",
  },
  en: {
    eyebrow: "Service Charter",
    title: "Our commitments, measurable",
    intro: "This Service Charter declares the quality levels SIMAMMA commits to across the platform, eSIM delivery and customer care. Values are targets measured on a monthly basis.",
    updated: "Last updated: September 21, 2026",
    slaTitle: "Service levels (SLA)",
    slaSub: "What you can always expect",
    sla: [
      { icon: "activity", value: "99.9%", label: "Platform availability", desc: "Monthly uptime target for site and app, with continuous monitoring and automatic intervention." },
      { icon: "zap", value: "< 2 min", label: "eSIM delivery", desc: "From payment to QR code in your account and inbox, 24 hours a day, holidays included." },
      { icon: "refresh", value: "< 5 min", label: "Data top-ups", desc: "Top-ups apply to your existing eSIM: no reinstall, no waiting while travelling." },
      { icon: "chat", value: "Instant", label: "AI assistant", desc: "The AI chat answers right away, 24/7, in 10 languages: plans, compatibility, orders and setup." },
      { icon: "mail", value: "< 24h bus.", label: "Email & contact form", desc: "Every written request gets a human reply within one business day." },
      { icon: "shield", value: "Automatic", label: "Refund if delivery fails", desc: "If the eSIM is not delivered due to a technical fault, the refund starts by itself: no forms, no waiting." },
      { icon: "file", value: "≤ 5 bus. days", label: "Complaint handling", desc: "Every complaint receives a tracked, reasoned reply within five business days." },
    ],
    chanTitle: "Contact channels & response times",
    chanSub: "Pick your preferred channel: the published times are our commitment",
    chanHead: ["Channel", "Best for", "Response time"],
    chan: [
      { icon: "chat", name: "AI assistant chat", for: "Plans, coverage, compatibility, order status", time: "Instant — 24/7" },
      { icon: "ticket", name: "Ticket from your account", for: "Registered customers: orders, top-ups, account", time: "Picked up within 2h, reply within 24 business hours" },
      { icon: "mail", name: "Email info@vediloo.it", for: "Billing, invoices, privacy, partnerships", time: "Within 24 business hours" },
      { icon: "phone", name: "Phone +39 06 84102697", for: "Urgent and administrative matters", time: "Office hours (CET)" },
      { icon: "send", name: "Form on this page", for: "Anyone, no account needed", time: "Instant confirmation, reply within 24 business hours" },
    ],
    formTitle: "Write to us now",
    formSub: "Fill in the form: you get an instant acknowledgement and a reply within 24 business hours.",
    fName: "Full name",
    fEmail: "Your email",
    fSubject: "Subject",
    fMessage: "Message",
    fSend: "Send message",
    fDoneTitle: "Message sent",
    fDoneBody: "Thank you! We've just emailed you the acknowledgement. We'll reply to",
    fDoneWithin: "within 24 business hours.",
    fAgain: "Send another message",
    noteTitle: "Methodology note",
    noteBody: "Service levels are measured monthly across SIMAMMA systems (site, app, API, eSIM delivery). Mobile data network availability and quality depend on local partner operators and are outside these SLAs. If we miss a response time, write to info@vediloo.it quoting this Charter.",
  },
};

const ICONS = { activity: Activity, zap: Zap, refresh: RefreshCw, chat: MessageCircle, mail: Mail, shield: ShieldCheck, file: FileWarning, ticket: Ticket, send: Send, phone: Phone };

export default function ServiceCharter() {
  const { t, lang, company } = useLanguage();
  usePageMeta("seo.charterTitle", "seo.charterDesc");
  const c = CONTENT[lang] || CONTENT.en;
  const hp = useHoneypot();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [captcha, setCaptcha] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (turnstileActive() && !captcha) {
      toast.error(t("misc.captcha"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contact", { ...form, lang, turnstile_token: captcha, ...hp.payload() });
      setDone(data);
    } catch (e2) {
      const detail = e2.response?.data?.detail || "";
      toast.error(detail === "invalid_email" ? t("news.invalid") : detail.startsWith("security_check") ? t("misc.captcha") : (t("misc.error")));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#FF6B35] transition-colors";

  return (
    <div data-testid="service-charter" className="px-4 sm:px-6 lg:px-12 max-w-5xl mx-auto py-12 sm:py-16">
      <Reveal>
        <Link to="/" data-testid="charter-back" className="inline-flex items-center gap-2 font-mono-t text-xs font-bold uppercase tracking-[0.2em] text-slate-400 hover:text-[#FF6B35] transition-colors">
          <ArrowLeft className="w-4 h-4" /> SIMAMMA
        </Link>
        <div className="mt-8 font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35]">{c.eyebrow}</div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900 mt-3">{c.title}</h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">{c.intro}</p>
        <p className="mt-3 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400">{c.updated}</p>
      </Reveal>

      <section data-testid="charter-sla" className="mt-14">
        <Reveal>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{c.slaTitle}</h2>
          <p className="mt-2 text-sm text-slate-500">{c.slaSub}</p>
        </Reveal>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {c.sla.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.label} delay={i * 0.05}>
                <div data-testid={`sla-card-${s.icon}`} className="h-full rounded-3xl bg-white border border-slate-200/80 shadow-sm p-6 hover:border-[#FF6B35]/50 hover:shadow-md transition-all">
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 rounded-full bg-[#FF6B35]/10 text-[#FF6B35] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-display font-extrabold text-2xl text-slate-900">{s.value}</span>
                  </div>
                  <div className="mt-3 font-display font-bold text-slate-900">{s.label}</div>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="charter-channels" className="mt-16">
        <Reveal>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{c.chanTitle}</h2>
          <p className="mt-2 text-sm text-slate-500">{c.chanSub}</p>
        </Reveal>
        <div className="mt-8 space-y-3">
          <div className="hidden sm:grid grid-cols-[1.1fr_1.4fr_1fr] gap-4 px-6 font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
            {c.chanHead.map((h) => <span key={h}>{h}</span>)}
          </div>
          {c.chan.map((ch, i) => {
            const Icon = ICONS[ch.icon];
            return (
              <Reveal key={ch.name} delay={i * 0.04}>
                <div data-testid={`channel-row-${ch.icon}`} className="grid sm:grid-cols-[1.1fr_1.4fr_1fr] gap-2 sm:gap-4 items-center rounded-2xl bg-white border border-slate-200/80 px-6 py-4">
                  <span className="flex items-center gap-2.5 font-display font-bold text-slate-900">
                    <Icon className="w-4 h-4 text-[#FF6B35] shrink-0" /> {ch.name}
                  </span>
                  <span className="text-sm text-slate-500">{ch.for}</span>
                  <span className="font-mono-t text-xs font-bold text-[#0D9488]">{ch.time}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="charter-contact" className="mt-16">
        <Reveal>
          <div className="rounded-[2rem] bg-white border border-slate-200/80 shadow-sm p-7 sm:p-10">
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-slate-900">{c.formTitle}</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-xl">{c.formSub}</p>
            {done ? (
              <div data-testid="contact-done" className="mt-8 rounded-2xl bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display font-bold text-emerald-800">{c.fDoneTitle}</div>
                  <p className="mt-1 text-sm text-emerald-700">{c.fDoneBody} <strong>{form.email}</strong> {c.fDoneWithin}</p>
                  <button type="button" data-testid="contact-again" onClick={() => { setDone(null); setForm({ name: "", email: "", subject: "", message: "" }); setCaptcha(null); }}
                    className="mt-3 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em] text-emerald-700 hover:text-emerald-900 underline underline-offset-4">
                    {c.fAgain}
                  </button>
                </div>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-4">
                <hp.Field />
                <input data-testid="contact-name" required minLength={2} maxLength={100} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={c.fName} className={inputCls} />
                <input data-testid="contact-email" required type="email" maxLength={120} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={c.fEmail} className={inputCls} />
                <input data-testid="contact-subject" required minLength={3} maxLength={150} value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder={c.fSubject} className={`${inputCls} sm:col-span-2`} />
                <textarea data-testid="contact-message" required minLength={10} maxLength={4000} rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={c.fMessage} className={`${inputCls} sm:col-span-2 resize-y`} />
                <div className="sm:col-span-2"><Turnstile onToken={setCaptcha} /></div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-4">
                  <button data-testid="contact-submit" type="submit" disabled={busy}
                    className="rounded-full bg-[#FF6B35] text-white px-8 py-3 min-h-[44px] font-mono-t text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#E8551F] transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    {c.fSend}
                  </button>
                  <span className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-400">&lt; 24h</span>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      <section data-testid="charter-note" className="mt-12">
        <Reveal>
          <div className="rounded-3xl border border-dashed border-slate-300 p-6">
            <h3 className="font-display font-bold text-slate-900">{c.noteTitle}</h3>
            <p className="mt-2 text-sm text-slate-500 leading-relaxed">{c.noteBody}</p>
            <p className="mt-4 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-400">
              {company?.name || "EI S.r.l."} — P.IVA {company?.vat || "16017671005"} · info@vediloo.it
            </p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
